import { Content } from "@/ui";
import { getRoute, getRouteIdArray } from "@/functions/getRoute";
import { pages_helper } from "@/helpers/pages_helper";
import { createTitle } from "@/functions/createTitle";
import { getNestedRoutes } from "@/functions/getNestedRoutes";
import { getRelatedJobs } from "@/functions/getRelatedJobs";
import sn from "@/snippets";

import useDynamicPage from "@/hooks/useDynamicPage";
import useDynamicPages from "@/hooks/useDynamicPages";
import { getDynamicPageStaticProps } from "@/functions/getDynamicPageStaticProps";
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { ContentBlocks } from "@/ui/ContentBlocks";

export default function Page({ content, prefix, title, seo, canonical, data, isBuilder = false }) {
  return (
    <>
      {isBuilder ? (
        <>
          <DynamicPageSeo
            {...seo}
            title={seo?.title.length > 0 ? seo.title : title}
            canonical={seo?.canonical.length > 0 ? seo.canonical : canonical}
          />
          <ContentBlocks data={data} prefix={prefix} />
        </>
      ) : (
        <Content items={content} />
      )}
    </>
  );
}

export async function getStaticProps({ params: { url_slugs } }) {
  const url_slug = url_slugs.join("/");
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const builderPage = useDynamicPage(url_slug);
  if (builderPage) {
    return getDynamicPageStaticProps({ page: builderPage, url_slug });
  }

  const pages = getNestedRoutes({ url_slugs });
  const [_page, prevPage] = [...pages].reverse();
  const page = pages_helper.nestedFind(url_slugs);
  const parent = page.parent.id ? pages_helper.find(page.parent.id, "id") : null;
  const siblings = page.parent.id ? pages_helper.fetch({ parent: page.parent.id }) : [];
  const children = pages_helper.fetch({ parent: page.id });
  const jobs = getRelatedJobs({ page });

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      meta: { title: createTitle(...pages.map((i) => i.label).reverse()) },
      content: [
        { component: "BreadcrumbNavigation", props: { items: pages } },
        sn.header({ page, prevPage }),
        sn.intro({ page }),
        ...(page.show_job_in_sidebar || page.show_sidebar_navigation
          ? [
              sn.gridButtons({ page }),
              {
                component: "ContentWithSidebar",
                props: {
                  content: [sn.collapsibleSection({ page }), sn.body({ page }), sn.form({ page })].filter((i) => !!i),
                  sidebar: [
                    ...(page.show_job_in_sidebar
                      ? [
                          { component: "QuickJobSearch", props: { stack: true } },
                          jobs && {
                            component: "LatestJobsSidebar",
                            props: { title: page.related_jobs_title ?? null, items: jobs.slice(0, 3) },
                          },
                        ]
                      : []),
                    ...(page.show_sidebar_navigation
                      ? [
                          {
                            component: "SidebarNavigation",
                            props: {
                              items: parent
                                ? [
                                    {
                                      label: parent.title,
                                      href: prevPage.href,
                                      children: siblings.map(({ id, title, url_slug }) => ({
                                        label: title,
                                        href: getRoute("page", { url_slugs: [...prevPage.href.split("/"), url_slug] }),
                                        active: id === page.id,
                                      })),
                                    },
                                  ]
                                : [
                                    {
                                      label: page["title"],
                                      href: _page.href,
                                      children: children.map(({ title, url_slug }) => ({
                                        label: title,
                                        href: getRoute("page", { url_slugs: [...url_slugs, url_slug] }),
                                      })),
                                    },
                                  ],
                            },
                          },
                        ]
                      : []),
                  ],
                },
              },

              // START - New Components
              sn.features({ page }),
              sn.featureTabs({ page }),
              sn.benefits({ page }),
              sn.trendingJobs({ page }),
              sn.tileGroups({ page }),
              sn.promotionalCarousel({ page }),
              sn.multiTiles({ page }),
              sn.multiCards({ page }),
              sn.statCounterWidget({ page }),
              // END - New Components

              ...sn.narrativePanels({ page }),
              sn.chatbot({ page }),
              sn.promoSection({ page }),
            ]
          : [
              sn.gridButtons({ page }),

              // START - New Components
              sn.features({ page }),
              sn.featureTabs({ page }),
              sn.benefits({ page }),
              sn.trendingJobs({ page }),
              sn.tileGroups({ page }),
              sn.promotionalCarousel({ page }),
              sn.multiTiles({ page }),
              sn.multiCards({ page }),
              sn.statCounterWidget({ page }),
              // END - New Components

              sn.collapsibleSection({ page }),
              sn.body({ page }),
              ...sn.narrativePanels({ page }),
              sn.form({ page }),
              sn.promoSection({ page }),
              ...(jobs && jobs.length > 0
                ? [
                    {
                      component: "LatestJobs",
                      props: {
                        title: page?.related_jobs_title ?? null,
                        items: jobs,
                        filters: page?.show_jobs_group_filters ?? false,
                      },
                    },
                  ]
                : []),
            ]),
        sn.blockQuote({ page }),
      ],
    },
  };
}

export async function getStaticPaths() {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const builderPaths = useDynamicPages()
    .filter((i) => !["/"].includes(i.url_slug))
    .map((i) => ({ params: { url_slugs: i.url_slug.split("/") } }));

  return {
    paths: [
      ...builderPaths,
      ...pages_helper.toNestedPaths({
        exclude: getRouteIdArray("branches"),
      }),
    ],
    fallback: false,
  };
}
